import PropTypes from 'prop-types';
import React from 'react';
import { useTheme } from '@glu/theming';
import '../../themeDefaults';
import ColorBar from '../ColorBar/ColorBar.jsx';
import useStyles from './ProgressBar.styles';

/**
 * The progress bar status
 * @typedef {('error'|'positive'|'stalled')} ProgressBarStatus
 */
export const PROGRESS_BAR_STATUS = {
  POSITIVE: 'positive',
  ERROR: 'error',
  STALLED: 'stalled'
};

function ProgressBar({
  className,
  dark,
  dataQa,
  message,
  progress,
  status,
  ...rest
}) {
  const classes = useStyles({ dark, rest });
  const { palette } = useTheme();

  const barColors = {
    positive: palette.money.light,
    error: palette.error,
    stalled: palette.grey.G500,
    background: dark ? palette.grey.G600 : palette.highlightBackground
  };

  return (
    <div className={`${className} ${classes.root}`} data-qa={dataQa}>
      <div className={classes.messaging}>
        <span>{message}</span>
        {status !== PROGRESS_BAR_STATUS.ERROR && (<span>{`${progress}%`}</span>)}
      </div>
      <ColorBar
        height={6}
        backgroundColor={barColors.background}
        segments={[{
          amount: progress,
          color: barColors[status]
        }]}
      />
    </div>
  );
}

ProgressBar.propTypes = {
  /** @type {string} - CSS class for the root element */
  className: PropTypes.string,
  /** @type {boolean} - Dark mode */
  dark: PropTypes.bool,
  /** @type {string} - The QA attribute for the root element */
  dataQa: PropTypes.string.isRequired,
  /** @type {string} - The message to show above the progress bar */
  message: PropTypes.string,
  /** @type {number} - The progress so far */
  progress: PropTypes.number.isRequired,
  /** @type {ProgressBarStatus} - The status of the progress, used for color of the bar */
  status: PropTypes.oneOf(Object.values(PROGRESS_BAR_STATUS))
};

ProgressBar.defaultProps = {
  className: '',
  dark: false,
  message: '',
  status: PROGRESS_BAR_STATUS.STALLED
};

export default ProgressBar;
